import ResourceFormatsEditor, { suggestResourceFormats } from "../../components/ResourceFormatsEditor";
import { isArchiveFormat } from "../../utils/resourceFormats";
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import { toast } from "sonner";
import { useUpdateProductMutation,
        useGetProductDetailsForEditQuery,
        useUploadFilesMutation } from '../../slices/productsApiSlice.js'
import { getUploadErrorMessage } from "../../utils/uploadToastMessages";


function ProductEditScreen() {

    const { id: productId }  = useParams()

    const [name, setName] = useState('')
    const [price, setPrice] = useState(0)
    const [image, setImage] = useState('')
    const [resourceFormats, setResourceFormats] = useState([])
    const [file, setFile] = useState('')
    const [brand, setBrand] = useState('')
    const [category, setCategory] = useState('')
    const [countInStock, setCountInStock] = useState(0)
    const [description, setDescription] = useState('')

    const { data: product, isLoading, error } = useGetProductDetailsForEditQuery(productId)

    const [updateProduct, { isLoading: loadingUpdate}] = useUpdateProductMutation()

    const [uploadImage, { isLoading: loadingUpload }] = useUploadFilesMutation()

    const navigate = useNavigate()

    async function submitHandler(e){
        e.preventDefault()
        const updatedProduct = {
            productId,
            name,
            price,
            file,
            resourceFormats,
            image,
            brand,
            category,
            countInStock,
            description,
        }
        
        const result = await updateProduct(updatedProduct)
        if (result.error) {
            toast.error(result.error)
        } else {
            toast.success('Product updated')
            navigate('/admin/productList')
        }
    }

    useEffect(() => {
        if (product) {
            setResourceFormats((product.resourceFormats?.length ? product.resourceFormats : product.detectedResourceFormats || []).filter((format) => !isArchiveFormat(format)))
            setName(product.name)
            setPrice(product.price)
            setImage(product.images?.[0] || product.image || '')
            setFile(product.files?.[0] || product.file || '')
            setBrand(product.brand)
            setCategory(product.category)
            setCountInStock(product.countInStock)
            setDescription(product.description)
        }
     }, [product])

     
    async function uploadFileHandler(e, fieldName) {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append(fieldName, selectedFile);

    try {
        const res = await uploadImage(formData).unwrap();
        toast.success("Upload successful");

        if (fieldName === "image") {
            setImage(res.image || res.paths?.image?.[0] || "");
        } else if (fieldName === "file") {
            setFile(res.file || res.paths?.file?.[0] || "");
            setResourceFormats((prev) => [...new Set([...prev, ...suggestResourceFormats([selectedFile])])]);
        }
    } catch (err) {
        toast.error(getUploadErrorMessage(err, "File upload failed"));
    }
}

 

  return (
    <div className="space-y-4">
      <Link
        to="/admin/productlist"
        className="inline-flex rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
      >
        Go Back
      </Link>

      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h1 className="mb-4 text-3xl font-semibold text-slate-900">Edit Product</h1>
        {loadingUpdate && <Loader />}
        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">{error}</Message>
        ) : (
          <form onSubmit={submitHandler} className="space-y-4">
            <ResourceFormatsEditor value={resourceFormats} onChange={setResourceFormats} />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
              <input
                type="text"
                placeholder="Enter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Price</label>
              <input
                type="number"
                placeholder="Enter price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Image URL</label>
              <input
                type="text"
                placeholder="Enter image url"
                value={image}
                onChange={(e) => setImage(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
              <input
                type="file"
                name="image"
                onChange={(e) => uploadFileHandler(e, "image")}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-sky-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-sky-700"
              />
            </div>
            {loadingUpload && <Loader />}

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Brand</label>
              <input
                type="text"
                placeholder="Enter brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Count in Stock</label>
              <input
                type="number"
                placeholder="Enter count in stock"
                value={countInStock}
                onChange={(e) => setCountInStock(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Category</label>
              <input
                type="text"
                placeholder="Enter category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
              <textarea
                rows={4}
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <button
              type="submit"
              className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700"
            >
              Update
            </button>
          </form>
        )}
      </div>
    </div>
  );

}

export default ProductEditScreen

